import { hasValue } from '../numbers';
import { SparePartBase, System } from '@/common/types/pms';
import { StoreItem } from '@/common/types/purchasing';
import { LubricantItemType } from '@/common/types/lubricant-latest-robs';
import { strToNumber } from '@/library/utils/numbers';
import { timeZoneOptions } from '@/common/utils/fixed.tsx';

export const displaySparePart = (sparePart?: SparePartBase, drawingNoClassName?: string) => {
  return (
    <span>
      {sparePart?.drawing_no && showSparePartsNumber(sparePart.drawing_no) ? (
        <span>
          <strong className={drawingNoClassName}>{sparePart.drawing_no}</strong>
          <span> - </span>
        </span>
      ) : (
        ''
      )}
      {sparePart?.part_no && showSparePartsNumber(sparePart.part_no)
        ? `${sparePart.part_no} - `
        : ''}
      {sparePart?.description || '-'}
    </span>
  );
};

export const displayStoreItem = (item?: StoreItem) => {
  return (
    <span>
      {item?.code ? (
        <span>
          <strong>{item.code}</strong>
          <span> - </span>
        </span>
      ) : (
        ''
      )}

      {item?.description || '-'}
    </span>
  );
};

export const displayLubricantItem = (item?: LubricantItemType) => {
  return (
    <span>
      {item?.code ? (
        <span>
          <strong>{item.code}</strong>
          <span> - </span>
        </span>
      ) : (
        ''
      )}

      {item?.description || '-'}
    </span>
  );
};

export const showSparePartsNumber = (value: number | string | null) => {
  return hasValue(value) && value !== '-';
};

export function displayPartyCompanyDefaultName<
  T extends { company_name?: string; full_name?: string } | null | undefined
>(company?: T) {
  return company?.company_name || company?.full_name || ''; // Trade Name
}

export function displayPartyCompanyLegalName<
  T extends { company_full_name?: string } | null | undefined
>(company?: T) {
  /* Functionality exclusively handled by the API:
    1. When the Legal name is not the same as the Trade Name: <Legal Name> - <Trade name>
    2. When the Legal name is the same as the Trade Name: <Legal Name>
  */

  return company?.company_full_name || '';
}

export function displayPartyPersonName<T extends { full_name?: string } | null | undefined>(
  party?: T
) {
  return party?.full_name || '';
}

export const displayVesselSystem = (system: System) => system.parent || system;

export const displayVesselSubSystem = (system: System) => (system.parent ? system : null);

export const displayTimezone = (timezone: string | number | null | undefined) => {
  if (timezone === null || timezone === undefined) {
    return null;
  }

  const parsedTimezone = strToNumber(timezone);

  return timeZoneOptions.find(el => el.id == `${parsedTimezone}`) || null;
};

/**
 * Removes the suffix from the end of a full path string if it exists.
 * This is useful for removing the last item name from a full hierarchical path.
 *
 * @param fullPath - The complete path string
 * @param suffix - The suffix to remove from the end (typically the item name with separator)
 * @returns The path without the suffix, or the original path if suffix not found at the end
 *
 * @example
 * removePathSuffix('Parent - Child - Item', ' - Item') // Returns 'Parent - Child'
 * removePathSuffix('Parent - Child', ' - Item') // Returns 'Parent - Child'
 */
export const removePathSuffix = (fullPath: string, suffix: string): string => {
  if (!fullPath || !suffix) return fullPath;

  return fullPath.endsWith(suffix) ? fullPath.slice(0, -suffix.length).trim() : fullPath;
};
